import React, { useEffect, useState } from "react";

type Servico = { id?: number | string | null; name: string };
type Pet = { id: number; nome: string };

function csrf(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
}

export default function AgendarEncontroModal({
  open, onClose, services, hostName,
}: { open: boolean; onClose: () => void; services: Servico[]; hostName: string }) {
  const [pets, setPets] = useState<Pet[]>([]);
  const [serviceId, setServiceId] = useState("");
  const [petIds, setPetIds] = useState<number[]>([]);
  const [times, setTimes] = useState(["", "", ""]);
  const [place, setPlace] = useState("casa_anfitriao");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(""); setSuccess("");
    fetch("/tutor/pets", { credentials: "same-origin", headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" } })
      .then(async (r) => { if (!r.ok) throw new Error("Não foi possível carregar seus pets."); return r.json(); })
      .then((j) => setPets(Array.isArray(j?.pets) ? j.pets : []))
      .catch((e) => setError(e.message));
  }, [open]);

  if (!open) return null;

  const validServices = services.filter((s) => Number.isSafeInteger(Number(s.id)) && Number(s.id) > 0);

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(""); setLoading(true);
    try {
      const horarioStrings = times.filter(Boolean).map((value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) throw new Error("Informe horários válidos.");
        return date.toISOString().replace(".000Z", "Z");
      });
      if (!serviceId || petIds.length === 0 || horarioStrings.length === 0) {
        throw new Error("Selecione o serviço, ao menos um pet e um horário.");
      }
      const response = await fetch("/encontros", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-TOKEN": csrf() },
        body: JSON.stringify({ anfitriao_servico_id: Number(serviceId), pet_ids: petIds, tipo_local: place, horarios: horarioStrings }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || Object.values(data?.errors ?? {}).flat().join(" ") || "Não foi possível solicitar o encontro.");
      setSuccess("Solicitação enviada. Acompanhe a resposta em Meus encontros.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao solicitar encontro.");
    } finally { setLoading(false); }
  }

  return (
    <div role="presentation" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section role="dialog" aria-modal="true" aria-label="Agendar encontro prévio" className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xl font-bold text-slate-900">Conhecer antes de reservar</h2>
          <button onClick={onClose} type="button" className="rounded-lg border px-3 py-1" aria-label="Fechar">Fechar</button>
        </div>
        <p className="mb-4 mt-2 text-sm text-slate-600">Agende gratuitamente uma apresentação com {hostName}. Não há reserva nem pagamento nesta etapa.</p>
        {success ? <div role="status" className="space-y-3 rounded-xl bg-green-50 p-4 text-green-900">{success}<div><a className="font-semibold underline" href="/encontros/meus">Meus encontros</a></div></div> :
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm font-semibold">Serviço
            <select required className="mt-1 w-full rounded-lg border p-3" value={serviceId} onChange={e => setServiceId(e.target.value)}>
              <option value="">Selecione o serviço</option>{validServices.map(s => <option value={String(s.id)} key={String(s.id)}>{s.name}</option>)}
            </select>
          </label>
          {validServices.length === 0 && <p className="text-sm text-amber-700">Este anfitrião não possui serviço habilitado para agendamento.</p>}
          <fieldset><legend className="text-sm font-semibold">Pets que participarão</legend>
            <div className="mt-2 space-y-2">{pets.map(p => <label className="flex gap-2 text-sm" key={p.id}><input type="checkbox" checked={petIds.includes(p.id)} onChange={e => setPetIds(ids => e.target.checked ? [...ids, p.id] : ids.filter(id => id !== p.id))} />{p.nome}</label>)}</div>
            {!pets.length && <a className="mt-2 inline-block text-sm font-semibold underline" href="/pets/novo">Cadastrar meu pet</a>}
          </fieldset>
          <fieldset><legend className="text-sm font-semibold">Até três opções de horário (hoje também é possível, com 30 minutos de antecedência)</legend>
            <div className="mt-2 space-y-2">{times.map((value, i) => <label className="block text-xs text-slate-600" key={i}>Opção {i + 1}<input className="mt-1 block w-full rounded-lg border p-3 text-sm" type="datetime-local" value={value} onChange={e => setTimes(old => old.map((v, k) => k === i ? e.target.value : v))} required={i === 0}/></label>)}</div>
          </fieldset>
          <label className="block text-sm font-semibold">Escolha o local
            <select className="mt-1 w-full rounded-lg border p-3" value={place} onChange={e => setPlace(e.target.value)}>
              <option value="casa_anfitriao">Local do anfitrião (endereço após confirmação)</option>
              <option value="local_publico">Local público a combinar na plataforma</option>
              <option value="online">Online, por câmera (sala de vídeo após confirmação)</option>
            </select>
          </label>
          {place === "online" && <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-900">Depois da confirmação, o tutor e o anfitrião receberão acesso a uma sala de vídeo externa (Jitsi Meet). A câmera e o microfone dependerão da permissão de cada dispositivo. Não compartilhe informações confidenciais durante a chamada.</p>}
          {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
          <button type="submit" disabled={loading || validServices.length === 0} className="w-full rounded-xl bg-green-700 px-5 py-3 font-semibold text-white disabled:opacity-50">{loading ? "Enviando..." : "Solicitar encontro gratuito"}</button>
        </form>}
      </section>
    </div>
  );
}
